import { Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { ViewportChangedEvent, GridApi, GridReadyEvent } from '@ag-grid-community/core';
import { AgGridModule } from '@ag-grid-community/angular';
import { TranslateModule } from '@ngx-translate/core';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { OrganizationService } from '../../../services/organization.service';
import { VisibilityGroupTransac } from '../../../models/visibilities-info';
import { TranslationService } from '../../../../shared/services/translation.service';
import { clientSideAgGridModules, CommonGridOptions } from '../../../../shared/datacenter/grid-options';
import { CommonColDef, MultiLineColDef } from '../../../../shared/datacenter/column-defs';
import { CheckboxHeaderComponent } from '../../../../shared/datacenter/header-components/checkbox-header.component';
import { ColumnType } from '../../../../shared/datacenter/column-type';
import { LoadingSpinnerCellComponent }
	from '../../../../shared/datacenter/cell-renderers/loading-spinner-cell.component';
import { MultiLineValueTypes } from '../../../../shared/datacenter/cell-renderers/multi-line-value-types';

const resizeObservable = fromEvent(globalThis, 'resize');
@Component({
	selector: 'copo-organization-card-transactional-visibilities',
	templateUrl: './organization-card-transactional-visibilities.component.html',
	styleUrl: './organization-card-transactional-visibilities.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [TranslateModule, AgGridModule]
})
export class OrganizationCardTransactionalVisibilitiesComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	transactionalVisibilities: VisibilityGroupTransac[] = [];
	tempTransactionalVisibilities?: VisibilityGroupTransac[];
	modifiedTransactionalVisibilities?: VisibilityGroupTransac[];
	gridApi?: GridApi<VisibilityGroupTransac>;
	resizeSub?: Subscription;
	readonly agGridModules = clientSideAgGridModules;
	gridInformationalVisibilities: CommonGridOptions<VisibilityGroupTransac>;

	private orgId?: string;
	private orgServiceSub: Subscription = Subscription.EMPTY;
	private readonly routeSub: Subscription = Subscription.EMPTY;

	constructor(
		private readonly organizationService: OrganizationService,
		private readonly activatedRoute: ActivatedRoute,
		private readonly translationService: TranslationService
	) {
		this.gridInformationalVisibilities = new CommonGridOptions<VisibilityGroupTransac>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			rowData: this.modifiedTransactionalVisibilities,
			suppressRowClickSelection: true,
			rowSelection: 'multiple',
			headerHeight: 70,
			domLayout: 'autoHeight',
			loadingOverlayComponent: LoadingSpinnerCellComponent,
			columnDefs: [
				new CommonColDef<VisibilityGroupTransac, string>({
					headerName: this.translationService.getTR('settings.organizations.name'),
					headerComponent: CheckboxHeaderComponent,
					sortable: true,
					field: 'client.name',
					sort: 'asc',
					filter: true,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					comparator: (valueA, valueB) =>
						!valueA ? -1 : !valueB ? 1 : valueA.toString().localeCompare(valueB.toString()),
					columnType: ColumnType.TEXT_COLUMN
				}),
				new CommonColDef<VisibilityGroupTransac>({
					headerName: this.translationService.getTR('settings.business-id'),
					headerComponent: CheckboxHeaderComponent,
					sortable: true,
					field: 'client.businessId',
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						return params.data.client.businessId.padStart(10, '0');
					},
					filter: true,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				}),
				new MultiLineColDef<VisibilityGroupTransac>({
					headerName: this.translationService.getTR('user.card.notifications.tab.service'),
					headerComponent: CheckboxHeaderComponent,
					sortable: true,
					fields: ['diffusionVisibilityGroupBocClient.serviceLabel'],
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const transactinalVisibility = params.data;
						return transactinalVisibility.diffusionVisibilityGroupBocClient.length > 0
							? transactinalVisibility.diffusionVisibilityGroupBocClient
								.map(item => item.serviceLabel).join(';') : '';
					},
					autoHeight: true,
					minWidth: 150,
					filter: true,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueType: MultiLineValueTypes.TEXT
				}),
				new CommonColDef<VisibilityGroupTransac>({
					headerName: this.translationService.getTR('datacenter-filter.organization-role'),
					headerComponent: CheckboxHeaderComponent,
					sortable: true,
					field: 'role',
					filter: true,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				}),
				new CommonColDef<VisibilityGroupTransac>({
					headerName: this.translationService.getTR('linked.soldto'),
					headerComponent: CheckboxHeaderComponent,
					sortable: true,
					field: 'client.businessId',
					valueGetter: params => {
						const transactionalVisibilityParamsData = params.data;
						if (!transactionalVisibilityParamsData) {
							return '';
						}
						for (const diffusionVisibilityGroupBocClient of
							transactionalVisibilityParamsData.diffusionVisibilityGroupBocClient) {
							if ('linkedSoldtoTransacVisibilityId' in diffusionVisibilityGroupBocClient) {
								for (const transactionalVisibility of this.transactionalVisibilities) {
									if (transactionalVisibility.id
										=== diffusionVisibilityGroupBocClient.linkedSoldtoTransacVisibilityId) {
										return transactionalVisibility.client.businessId.padStart(10, '0');
									}
								}
							}
						}
						return '';
					},
					filter: true,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			]
		});
		if (this.activatedRoute.parent === null) {
			return;
		}
		this.routeSub = this.activatedRoute.parent.paramMap.subscribe(params => {
			const idString = params.get('id');
			if (idString === null) {
				return;
			}
			this.orgId = idString;
		});
		if (this.orgId) {
			this.getTransactionalVisibilities(this.orgId);
		}
	}

	getTransactionalVisibilities(orgId: string) {
		this.orgServiceSub = this.organizationService.getOrganizationVisibilities(
			orgId
		)
			.subscribe({
				next: orgVisibilities => {
					this.tempTransactionalVisibilities = [];
					this.modifiedTransactionalVisibilities = [];
					if (this.gridApi) {
						if (orgVisibilities.results[0]?.visibilityGroupTransac) {
							this.transactionalVisibilities = orgVisibilities.results[0].visibilityGroupTransac;
							for (const visibility of this.transactionalVisibilities) {
								if (visibility.isEndUser === 1 && visibility.isPayer === 0
									&& visibility.isShipTo === 0 && visibility.isSoldTo === 0) {
									visibility.role = this.translationService.getTR('eoffer.enduser');
								} else if (visibility.isPayer === 1 && visibility.isShipTo === 0
									&& visibility.isSoldTo === 0) {
									visibility.role = this.translationService.getTR('commercial-documents.payer');
								} else if (visibility.isSoldTo === 1 && visibility.isPayer === 0
									&& visibility.isShipTo === 0) {
									visibility.role = this.translationService.getTR('commercial-documents.soldto');
								} else {
									visibility.role = this.translationService.getTR('batch.shipto');
								}
								if (visibility.isSoldTo === 1 || visibility.isPayer === 1) {
									this.tempTransactionalVisibilities.push(visibility);
								}
							}
							for (const tempTransacVisibility of this.tempTransactionalVisibilities) {
								const linkedMembers = this.transactionalVisibilities
									.filter(tV => this.hasLinked(tV, tempTransacVisibility));
								this.modifiedTransactionalVisibilities.push(tempTransacVisibility, ...linkedMembers);
							}
							this.gridApi.setFilterModel(null);
							this.gridApi.setGridOption('rowData', this.modifiedTransactionalVisibilities);
						} else {
							this.gridApi.setFilterModel(null);
							this.gridApi.setGridOption('rowData', []);
						}
					}
				},
				error: (err: unknown) => {
					console.error('Error from http get organization visibilities', err);
				}
			});
	}

	hasLinked(tV: VisibilityGroupTransac, tempTransactionalVisibilities: VisibilityGroupTransac) {
		if ((tV.isShipTo === 1 || tV.isEndUser === 1) && tempTransactionalVisibilities.isSoldTo === 1
			&& tV.diffusionVisibilityGroupBocClient.length > 0
			&& tV.diffusionVisibilityGroupBocClient[0].linkedSoldtoTransacVisibilityId
			=== tempTransactionalVisibilities.id) {
			return tV;
		}
		return null;
	}

	ngOnDestroy() {
		this.orgServiceSub.unsubscribe();
		this.routeSub.unsubscribe();
		if (this.resizeSub) {
			this.resizeSub.unsubscribe();
		}
	}

	onGridReady(params: GridReadyEvent) {
		this.gridApi = params.api;
		this.resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
	}

	onViewportChanged(params: ViewportChangedEvent) {
		params.api.sizeColumnsToFit();
	}
}
